// Client-side matching of the user's followed feeds for the search suggestions. The feeds are in
// memory already, so this costs no request. More lenient than the catalog autocomplete (which only
// matches title / domain prefixes): any word of the title may start the match, and a single
// character is enough.

export interface FollowedFeedSearchable {
  title: string;
  shortTitle?: string;
  siteUrl?: string;
  url?: string;
}

// Letters (of any script) and digits only. Built at runtime: the TS target predates the 'u' flag.
const NON_ALNUM = new RegExp('[^\\p{L}\\p{N}]', 'gu');
const norm = (s: string) => s.toLowerCase().replace(NON_ALNUM, '');

const hostOf = (url?: string) => {
  try {
    return url ? new URL(url).hostname.replace(/^www\./, '') : '';
  } catch {
    return '';
  }
};

/** The site's host for display (without "www."), from the site URL or else the feed URL. */
export const feedDomain = (feed: FollowedFeedSearchable) => hostOf(feed.siteUrl) || hostOf(feed.url);

// 0: a title starts with the term, 1: a later word of a title starts it (the term may span words,
// e.g. "verge" or "ars tech"), 2: the site's domain starts with it; undefined: no match.
const matchRank = (feed: FollowedFeedSearchable, term: string): number | undefined => {
  const titles = [feed.shortTitle, feed.title].filter((t): t is string => !!t);
  if (titles.some(t => norm(t).startsWith(term))) return 0;
  if (titles.some(t => {
    const words = t.split(/[\s\-_.:,;/|]+/);
    return words.some((_, i) => i && norm(words.slice(i).join('')).startsWith(term));
  })) return 1;
  if ([hostOf(feed.siteUrl), hostOf(feed.url)].some(h => h && norm(h).startsWith(term))) return 2;
  return undefined;
};

/** The followed feeds matching the search input, best matches first, then alphabetically. */
export function searchFollowedFeeds<F extends FollowedFeedSearchable>(feeds: F[], input: string, max: number): F[] {
  const term = norm(input);
  if (!term) return [];
  return feeds
    .map(feed => ({ feed, rank: matchRank(feed, term) }))
    .filter((m): m is { feed: F; rank: number } => m.rank != undefined)
    .sort((a, b) => a.rank - b.rank || (a.feed.shortTitle || a.feed.title).localeCompare(b.feed.shortTitle || b.feed.title))
    .slice(0, max)
    .map(m => m.feed);
}
